import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';

import { SyncWithVideoButton } from './sync-with-video-button';
import { TranscriptHeader } from './transcript-header';
import { TranscriptItem } from './transcript-item';
import { useScrollToTranscriptItem } from './use-scroll-to-transcript-item';
import { useTranscriptSearch } from './use-transcript-search';
import { getCurrentActiveCue } from './utils';
import { OptionsProcessor } from '../options-processer/options-processor';
import { PlayerApi, PlayerConfig, PlayerEvent } from '../player';
import { Transcript, TranscriptCue } from '../player/player.interface';
import { SubtitleOption } from '../settings-menu/settings-menu.types';

import styles from './transcript-panel.module.css';
import transcriptStyles from './transcript.module.css';

const TRANSCRIPT_ITEM_ID_PREFIX = 'transcript-panel-item-';

type TranscriptPanelProps = {
  transcript?: Transcript | null;
  currentTime?: number;
  languages?: SubtitleOption[];
  currentLanguage?: string;
  onSelectedLanguageChange?: (language: string) => void;

  onClose?: () => void;

  playerConfig: PlayerConfig;
  playerApi?: PlayerApi;
  shown?: boolean;
};

export function TranscriptPanel({
  transcript,
  languages,
  currentLanguage,
  onSelectedLanguageChange,
  onClose,
  playerConfig,
  playerApi,
  shown,
}: TranscriptPanelProps) {
  const [currentActiveCue, setCurrentActiveCue] = useState<TranscriptCue | undefined>();
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(false);

  const showTranscriptDownload = playerConfig?.settings?.hideTranscriptDownload !== true;

  // Get the current active cue based on the current time (via PlayerApi)
  const lastCurrentActiveCue = useRef<TranscriptCue | undefined>(); // Used to avoid unnecessary re-renders
  useEffect(() => {
    const onTimeUpdate = () => {
      const currentTime = (playerApi?.getCurrentTime() ?? 0) * 1000;
      const activeCue = getCurrentActiveCue(transcript?.cues, currentTime);

      if (lastCurrentActiveCue.current !== activeCue) {
        setCurrentActiveCue(activeCue);
        lastCurrentActiveCue.current = activeCue;
      }
    };

    if (playerApi && transcript?.cues) {
      onTimeUpdate();
      playerApi.addEventListener(PlayerEvent.TIME_UPDATE, onTimeUpdate);

      return () => playerApi.removeEventListener(PlayerEvent.TIME_UPDATE, onTimeUpdate);
    }
  }, [playerApi, transcript?.cues]);

  const { scrollRef, scrollToTranscriptItem } = useScrollToTranscriptItem(TRANSCRIPT_ITEM_ID_PREFIX);

  const { search, onSearch, cuesIndex, searchResultIndex, currentSearchResult, setCurrentSearchResult } =
    useTranscriptSearch(transcript, scrollToTranscriptItem);

  const onTranscriptItemClick = useCallback(
    (cue: TranscriptCue) => {
      if (playerApi) {
        playerApi.setCurrentTime(cue.startTime / 1000);

        // Enable auto-scroll on transcript item click. We need to wait a bit since jumping to timestamp takes longer
        setTimeout(() => {
          setAutoScrollEnabled(true);
        }, 100);
      }
    },
    [playerApi]
  );

  return (
    <OptionsProcessor options={playerConfig}>
      <div data-testid="transcript-panel" className={classNames(transcriptStyles.wrapper, styles.wrapper)}>
        <TranscriptHeader
          languages={languages}
          selectedLanguage={currentLanguage}
          onSelectedLanguageChange={onSelectedLanguageChange}
          onClose={onClose}
          onSearch={onSearch}
          searchResultCount={searchResultIndex?.length}
          currentSearchResult={currentSearchResult}
          setCurrentSearchResult={setCurrentSearchResult}
          showTranscriptDownload={showTranscriptDownload}
        />
        <div
          role="menu"
          data-testid="transcript-content"
          className={classNames(styles.content, 'player-scrollbar')}
          ref={scrollRef}
        >
          {transcript?.cues?.map((cue, index) => {
            const id = `${TRANSCRIPT_ITEM_ID_PREFIX}${index}`;

            return (
              <TranscriptItem
                id={id}
                key={id}
                data-testid={id}
                cue={cue}
                isActive={!!currentActiveCue && currentActiveCue.startTime === cue.startTime}
                onItemClick={onTranscriptItemClick}
                search={search}
                activeHighlightedIndex={cuesIndex?.[index]?.indexOf((currentSearchResult ?? 0) - 1)}
              />
            );
          })}

          <SyncWithVideoButton
            itemIdPrefix={TRANSCRIPT_ITEM_ID_PREFIX}
            currentActiveCue={currentActiveCue}
            cues={transcript?.cues}
            scrollToTranscriptItem={scrollToTranscriptItem}
            scrollRef={scrollRef}
            shown={shown}
            autoScrollEnabled={autoScrollEnabled}
            setAutoScrollEnabled={setAutoScrollEnabled}
          />
        </div>
      </div>
    </OptionsProcessor>
  );
}
